import { supabase } from './supabase';
import { submit, registerOp, setLabelResolver } from './outbox';

// Человекочитаемые подписи для плашки «Не сохранено»: какой маршрут и какой день.
const routeNames = {};
export function rememberRoutes(list) {
  (list || []).forEach(r => { if (r && r.id) routeNames[r.id] = r.external_code || r.internal_code || r.name || String(r.id).slice(0, 8); });
}
const fmtD = (d) => (d ? `${String(d).slice(8, 10)}.${String(d).slice(5, 7)}` : '');
setLabelResolver((op, p) => {
  if (op === 'setStatus')     return `${routeNames[p.routeId] || 'маршрут'} · ${fmtD(p.date)} · статус ${p.statusCode}${p.planVisits != null ? ', план ' + p.planVisits : ''}`;
  if (op === 'clearStatus')   return `${routeNames[p.routeId] || 'маршрут'} · ${fmtD(p.date)} · очистка статуса`;
  if (op === 'setOrderVisits') return `${routeNames[p.routeId] || 'маршрут'} · ${fmtD(p.date)} · заказ ${p.visits}`;
  if (op === 'setHyperPlan')  return `гиппер · ${fmtD(p.date)} · план ${p.visits}`;
  return '';
});

// Supabase REST отдаёт максимум 1000 строк за запрос — читаем весь список постранично.
// Нужно везде, где тянем полную таблицу (routes ~1287, merchandisers ~1287 и т.п.).
export async function selectAll(table, columns, applyFilters) {
  const pageSize = 1000;
  let from = 0;
  let all = [];
  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Авто-повтор страницы при кратком сетевом сбое («Failed to fetch» и т.п.).
    // Запрос пересобираем на каждой попытке (builder одноразовый). Чтение
    // идемпотентно — повтор безопасен и ничего не меняет в базе.
    const { data, error } = await withRetry(() => {
      let q = supabase.from(table).select(columns).range(from, from + pageSize - 1);
      if (applyFilters) q = applyFilters(q);
      return q;
    });
    if (error) throw error;
    all = all.concat(data || []);
    if (!data || data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

// Повтор идемпотентной записи при кратковременном сбое (таймаут/сеть/нехватка
// ресурсов под нагрузкой — те самые ~1.6% ошибок). upsert по onConflict и delete
// идемпотентны, поэтому повтор безопасен и не создаёт дублей.
async function withRetry(fn, tries = 3) {
  let lastErr;
  for (let i = 0; i < tries; i++) {
    try {
      const res = await fn();
      // supabase-js возвращает { error }, а не бросает — обработаем оба случая
      if (res && res.error) throw res.error;
      return res;
    } catch (e) {
      lastErr = e;
      if (i < tries - 1) await new Promise(r => setTimeout(r, 200 * (i + 1)));
    }
  }
  throw lastErr;
}

// ── ПЕРИОД ────────────────────────────────────────────────────────────────────

export async function getActivePeriod() {
  const { data, error } = await supabase
    .from('work_periods')
    .select('*, work_weeks(*)')
    .eq('is_active', true)
    .single();
  if (error) throw error;
  return data;
}

export async function getAllPeriods() {
  const { data, error } = await supabase
    .from('work_periods')
    .select('*')
    .order('year', { ascending: false })
    .order('month', { ascending: false });
  if (error) throw error;
  return data;
}

// ── МАРШРУТЫ И МРЧ ────────────────────────────────────────────────────────────

// Маршруты СВ (для личного табеля СВ)
export async function getRoutesBySv(svId) {
  // Постранично (selectAll): у крупного СВ маршрутов может быть >1000, одиночный
  // запрос обрезался бы на 1000 и часть маршрутов пропадала бы со страницы.
  // svId может быть массивом: СВ с маршрутами в командах разных ТМ заведён несколькими
  // записями supervisors на одного пользователя — личный табель СВ собирает их все.
  return selectAll('routes', '*, merchandisers(*)',
    q => (Array.isArray(svId) ? q.in('sv_id', svId) : q.eq('sv_id', svId)).eq('is_active', true).neq('is_hidden', true).order('internal_code'));
}

// Все маршруты ТМ (через СВ)
export async function getRoutesByTm(tmId) {
  const { data, error } = await supabase
    .from('routes')
    .select(`
      *,
      merchandisers(*),
      supervisors!sv_id(id, full_name, internal_code, user_id)
    `)
    .eq('supervisors.tm_id', tmId)
    .eq('is_active', true)
    .neq('is_hidden', true);
  if (error) throw error;
  return (data || []).filter(r => !r.is_pm);   // #6 — ПМ не показываем на стороне ТМ
}

// Все СВ ТМ
// id супервайзера по user_id учётки. Возвращает id или null, если привязки НЕТ
// (0 строк). Сбой сети/таймаут не маскируется под «не привязан»: после ретраев
// ошибка пробрасывается наверх. limit(1) вместо .single(), чтобы дубль привязки
// не ломал вход.
export async function getSvIdByUser(userId) {
  const { data } = await withRetry(() =>
    supabase.from('supervisors').select('id').eq('user_id', userId).limit(1));
  return (data && data[0]) ? data[0].id : null;
}

// Все записи СВ учётки (основная — первой). Нужны, когда маршруты СВ разнесены по ТМ.
export async function getSvIdsByUser(userId) {
  const { data } = await withRetry(() =>
    supabase.from('supervisors').select('id, internal_code').eq('user_id', userId).eq('is_active', true));
  return (data || []).sort((a, b) => a.internal_code.length - b.internal_code.length).map(x => x.id);
}

export async function getSvByTm(tmId) {
  const { data, error } = await supabase
    .from('supervisors')
    .select('*, users!user_id(login, full_name)')
    .eq('tm_id', tmId)
    .eq('is_active', true)
    .order('full_name');
  if (error) throw error;
  return data;
}

// Все ТМ (для РМ/admin)
export async function getAllTms() {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .in('role', ['tm'])
    .eq('is_active', true)
    .order('full_name');
  if (error) throw error;
  return data;
}

// ── СТАТУСЫ ───────────────────────────────────────────────────────────────────

// Получить все статусы за период для маршрутов СВ
export async function getStatusesBySv(periodId, svId) {
  const routes = await getRoutesBySv(svId);
  const routeIds = routes.map(r => r.id);
  if (!routeIds.length) return { routes, statuses: {} };

  // Чанки + пагинация (иначе ответ режется на 1000 строк).
  const data = await selectByRouteChunks('daily_statuses', '*', periodId, routeIds);

  // Группируем: { route_id: { 'YYYY-MM-DD': status_code } }
  const map = {};
  for (const s of data) {
    if (!map[s.route_id]) map[s.route_id] = {};
    map[s.route_id][s.work_date] = s.status_code;
  }
  return { routes, statuses: map };
}

// Поставить/обновить статус (planVisits — число визитов прогноза, опционально)
async function setStatusNow({ periodId, routeId, date, statusCode, planVisits = null, userId }) {
  const payload = {
    period_id:   periodId,
    route_id:    routeId,
    work_date:   date,           // 'YYYY-MM-DD'
    status_code: statusCode,
    set_by:      userId,
    updated_by:  userId,
    updated_at:  new Date().toISOString(),
  };
  if (planVisits !== undefined) payload.plan_visits = planVisits;
  // Без .select().single() (возврат строки не нужен) + авто-повтор при кратком сбое.
  await withRetry(() => supabase
    .from('daily_statuses')
    .upsert(payload, { onConflict: 'period_id,route_id,work_date' }));
}

registerOp('setStatus', (p) => setStatusNow(p));
export async function setStatus(args) {
  const key = `st|${args.periodId}|${args.routeId}|${args.date}`;
  return submit('setStatus', { ...args, _ts: Date.now() }, key);
}

// ── МЕТРИКИ ДЛЯ ДАШБОРДОВ ТМ/РМ: заказ/прогноз/факт + дисциплина по маршрутам ──
// Загружает по набору маршрутов orders/daily_statuses(plan_visits)/daily_visit_facts.
// Держать routeIds небольшими (в РМ вызывать по каждому СВ), чтобы не упереться в длину URL.
// Грузит таблицу по маршрутам мелкими пачками id (по 40), чтобы URL не был
// слишком длинным (иначе шлюз рубит запрос 400 Bad Request).
async function selectByRouteChunks(table, columns, periodId, routeIds, chunk = 40) {
  let all = [];
  for (let i = 0; i < routeIds.length; i += chunk) {
    const part = routeIds.slice(i, i + chunk);
    const rows = await selectAll(table, columns, q => q.eq('period_id', periodId).in('route_id', part));
    all = all.concat(rows);
  }
  return all;
}

export async function getPeriodDataForRoutes(periodId, routeIds) {
  if (!routeIds.length) return { orders: {}, statuses: {}, facts: {} };

  // Каждый источник грузим независимо: ошибка одного (нет таблицы/колонки/данных)
  // не должна обнулять остальные метрики.
  async function safe(label, fn) {
    try { return await fn(); }
    catch (e) { console.error(`[getPeriodDataForRoutes] источник "${label}" не загружен:`, e?.message || e); return null; }
  }

  const orders = {}, statuses = {}, facts = {};

  const orderRows = await safe('orders', () =>
    selectByRouteChunks('orders', 'route_id, work_date, visits', periodId, routeIds));
  (orderRows || []).forEach(o => { (orders[o.route_id] || (orders[o.route_id] = {}))[o.work_date] = o.visits; });

  // Статусы: сначала пробуем с plan_visits, при ошибке — без него (колонка может быть не добавлена).
  let statusRows = await safe('daily_statuses+plan_visits', () =>
    selectByRouteChunks('daily_statuses', 'route_id, work_date, status_code, plan_visits', periodId, routeIds));
  if (statusRows == null) {
    statusRows = await safe('daily_statuses', () =>
      selectByRouteChunks('daily_statuses', 'route_id, work_date, status_code', periodId, routeIds));
  }
  (statusRows || []).forEach(s => { (statuses[s.route_id] || (statuses[s.route_id] = {}))[s.work_date] = { code: s.status_code, plan_visits: s.plan_visits }; });

  const factRows = await safe('daily_visit_facts', () =>
    selectByRouteChunks('daily_visit_facts', 'route_id, work_date, visits_fact', periodId, routeIds));
  (factRows || []).forEach(f => { (facts[f.route_id] || (facts[f.route_id] = {}))[f.work_date] = f.visits_fact; });

  return { orders, statuses, facts };
}

function planVisitsOfStatus(st, orderVisits) {
  if (!st || !st.code) return null;
  const c = st.code;
  if (c === 'ПЛ ВЫХ') return null;
  if (c.endsWith('-') || c.endsWith('−')) return 0;
  return st.plan_visits != null ? st.plan_visits : (orderVisits != null ? orderVisits : 0);
}

// Метрики одного маршрута за набор дат.
// { order, plan, fact, exits, overlaps, sick, vac, vacDays, isVacancy }
export function computeRouteMetrics(route, dates, orders, statuses, facts) {
  const O = orders[route.id] || {}, S = statuses[route.id] || {}, F = facts[route.id] || {};
  let order = 0, plan = 0, fact = 0, exits = 0, overlaps = 0, sick = 0, vac = 0, vacDays = 0;
  dates.forEach(d => {
    const o = O[d] || 0; order += o;
    const st = S[d]; const pv = planVisitsOfStatus(st, o); if (pv != null) plan += pv;
    const f = F[d]; if (f != null) fact += f;
    if (st && st.code) {
      const c = st.code;
      if (c === '1' || c === 'СВ') exits++;
      if (c === 'БЛ-') sick++;
      if (c === 'ОТ-') vac++;
      if (c.indexOf('ВК') === 0) vacDays++;
      if (/^(БЛ|ОТ|ДО|ВК)\+/.test(c)) overlaps++;
    }
  });
  return { order, plan, fact, exits, overlaps, sick, vac, vacDays, isVacancy: !!route.is_vacancy || vacDays > 0 };
}

// Свернуть метрики массива маршрутов в агрегат (для СВ/ТМ/РМ)
export function aggregateMetrics(routes, dates, orders, statuses, facts) {
  const acc = { order: 0, plan: 0, fact: 0, exits: 0, overlaps: 0, sick: 0, vac: 0, vacDays: 0, vacRoutes: 0, routes: 0 };
  routes.forEach(r => {
    const m = computeRouteMetrics(r, dates, orders, statuses, facts);
    acc.order += m.order; acc.plan += m.plan; acc.fact += m.fact; acc.exits += m.exits;
    acc.overlaps += m.overlaps; acc.sick += m.sick; acc.vac += m.vac; acc.vacDays += m.vacDays;
    if (m.isVacancy) acc.vacRoutes++;
    acc.routes++;
  });
  return acc;
}

// ── ОПТИМИЗИРОВАННАЯ ЗАГРУЗКА: свёртка маршрут×неделя из БД (RPC) ─────────────
// Возвращает { route_id: { week_number: {ord,plan,fact,exits,overl,sick,vac,vacDays} } }
export async function getRouteWeekAgg(periodId, frozen = false, routeIds = null) {
  // Функция возвращает по строке на КАЖДЫЙ маршрут×неделя, а Supabase REST режет
  // ответ по 1000 строк — читаем постранично через .range(). routeIds (область
  // видимости ТМ/СВ) ограничивает агрегат только нужными маршрутами: тогда строк
  // мало, всё влезает в один вызов, и функция не пересчитывает всё по 5 раз.
  // routeIds=null — все маршруты (РМ/admin). frozen=true — из снимка «заморозки».
  const pageSize = 1000;
  let from = 0;
  const rw = {};
  const params = { p_period_id: periodId, p_frozen: frozen };
  if (routeIds && routeIds.length) params.p_route_ids = routeIds;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await supabase
      .rpc('dash_route_week', params)
      .order('route_id', { ascending: true })
      .order('week_number', { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    (data || []).forEach(r => {
      (rw[r.route_id] || (rw[r.route_id] = {}))[r.week_number] = {
        ord: r.ord, plan: r.plan, fact: r.fact, exits: r.exits,
        overl: r.overl, sick: r.sick, vac: r.vac, vacDays: r.vac_days,
      };
    });
    if (!data || data.length < pageSize) break;
    from += pageSize;
  }
  return rw;
}

// То же, но по дням не позже until (YYYY-MM-DD): для сравнения плана с фактом по идущей неделе
export async function getRouteWeekAggTo(periodId, frozen, routeIds, until) {
  // Функция возвращает по строке на КАЖДЫЙ маршрут×неделя, а Supabase REST режет
  // ответ по 1000 строк — читаем постранично через .range(). routeIds (область
  // видимости ТМ/СВ) ограничивает агрегат только нужными маршрутами: тогда строк
  // мало, всё влезает в один вызов, и функция не пересчитывает всё по 5 раз.
  // routeIds=null — все маршруты (РМ/admin). frozen=true — из снимка «заморозки».
  const pageSize = 1000;
  let from = 0;
  const rw = {};
  const params = { p_period_id: periodId, p_frozen: frozen, p_until: until };
  params.p_route_ids = (routeIds && routeIds.length) ? routeIds : null;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await supabase
      .rpc('dash_route_week_to', params)
      .order('route_id', { ascending: true })
      .order('week_number', { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    (data || []).forEach(r => {
      (rw[r.route_id] || (rw[r.route_id] = {}))[r.week_number] = {
        ord: r.ord, plan: r.plan, fact: r.fact, exits: r.exits,
        overl: r.overl, sick: r.sick, vac: r.vac, vacDays: r.vac_days,
      };
    });
    if (!data || data.length < pageSize) break;
    from += pageSize;
  }
  return rw;
}

// Фолбэк: собрать ту же свёртку из сырых данных (если RPC-функции нет в БД)
export function buildRouteWeekAggRaw(routes, weeks, weekNums, pdata) {
  const { orders, statuses, facts } = pdata;
  const rw = {};
  routes.forEach(r => {
    const byW = {};
    weekNums.forEach(w => {
      const dates = (weeks[w]?.days || []).map(d => d.date);
      const m = computeRouteMetrics(r, dates, orders, statuses, facts);
      byW[w] = { ord: m.order, plan: m.plan, fact: m.fact, exits: m.exits, overl: m.overlaps, sick: m.sick, vac: m.vac, vacDays: m.vacDays };
    });
    rw[r.id] = byW;
  });
  return rw;
}

// Агрегат по набору маршрутов за набор недель — из свёртки rweeks.
// Возвращает ту же форму, что aggregateMetrics.
export function aggregateWeeks(routes, weekNums, rweeks) {
  const acc = { order: 0, plan: 0, fact: 0, exits: 0, overlaps: 0, sick: 0, vac: 0, vacDays: 0, vacRoutes: 0, activeR: 0, routes: 0 };
  routes.forEach(r => {
    const byW = rweeks[r.id] || {};
    let rv = 0, rExits = 0, rOverl = 0;
    weekNums.forEach(w => {
      const c = byW[w]; if (!c) return;
      acc.order += c.ord || 0; acc.plan += c.plan || 0; acc.fact += c.fact || 0;
      acc.exits += c.exits || 0; acc.overlaps += c.overl || 0; acc.sick += c.sick || 0;
      acc.vac += c.vac || 0; acc.vacDays += c.vacDays || 0; rv += c.vacDays || 0;
      rExits += c.exits || 0; rOverl += c.overl || 0;
    });
    acc.routes++;
    // «Активный» маршрут: есть присутствие в табеле — выход или перекрытие
    if (rExits > 0 || rOverl > 0) acc.activeR++;
    if (r.is_vacancy || rv > 0) acc.vacRoutes++;
  });
  return acc;
}

// ── ТАБЕЛЬ СВ (новая модель): Заказ / Прогноз(статус+визиты) / Факт BI ─────────
export async function getTabellBySv(periodId, svId) {
  const routes = await getRoutesBySv(svId);
  const routeIds = routes.map(r => r.id);
  if (!routeIds.length) return { routes, statuses: {}, orders: {}, facts: {} };

  const [statusRows, orders, factRows] = await Promise.all([
    selectByRouteChunks('daily_statuses', 'route_id, work_date, status_code, plan_visits', periodId, routeIds),
    getOrdersByRoutes(periodId, routeIds),
    getVisitFactsByRoutes(periodId, routeIds),
  ]);

  const statuses = {};
  (statusRows || []).forEach(s => {
    if (!statuses[s.route_id]) statuses[s.route_id] = {};
    statuses[s.route_id][s.work_date] = { code: s.status_code, plan_visits: s.plan_visits };
  });
  const facts = {};
  (factRows || []).forEach(f => {
    if (!facts[f.route_id]) facts[f.route_id] = {};
    facts[f.route_id][f.work_date] = f.visits_fact;
  });
  return { routes, statuses, orders, facts };
}

// Удалить статус
async function clearStatusNow({ periodId, routeId, date }) {
  await withRetry(() => supabase
    .from('daily_statuses')
    .delete()
    .eq('period_id', periodId)
    .eq('route_id', routeId)
    .eq('work_date', date));
}

registerOp('clearStatus', (p) => clearStatusNow(p));
export async function clearStatus(args) {
  return submit('clearStatus', args, `st|${args.periodId}|${args.routeId}|${args.date}`);
}

// Batch-сохранение (для первоначальной загрузки)
export async function batchSetStatuses(statuses) {
  const { error } = await supabase
    .from('daily_statuses')
    .upsert(statuses, { onConflict: 'period_id,route_id,work_date' });
  if (error) throw error;
}

// ── СВОДНЫЕ ───────────────────────────────────────────────────────────────────

// Итоги по неделям для маршрутов СВ
export async function getWeekSummaryBySv(periodId, svId) {
  const routes = await getRoutesBySv(svId);
  const routeIds = routes.map(r => r.id);

  const { data, error } = await supabase
    .from('week_summary')
    .select('*')
    .eq('period_id', periodId)
    .in('route_id', routeIds);
  if (error) throw error;
  return data;
}

// Итоги по СВ для ТМ
export async function getSvWeekSummaryByTm(periodId, tmId) {
  const { data, error } = await supabase
    .from('sv_week_summary')
    .select('*, supervisors!sv_id(full_name, internal_code)')
    .eq('period_id', periodId)
    .eq('supervisors.tm_id', tmId);
  if (error) throw error;
  return data;
}

// Итоги по ТМ для РМ
export async function getTmWeekSummary(periodId) {
  const { data, error } = await supabase
    .from('tm_week_summary')
    .select('*, users!tm_id(full_name)')
    .eq('period_id', periodId);
  if (error) throw error;
  return data;
}

// ── ОТПУСКА ───────────────────────────────────────────────────────────────────

// #2 — отпуска читаем по ПЕРЕСЕЧЕНИЮ дат с периодом (а не по period_id),
// чтобы переходящий отпуск (напр. 28.09–05.10) был виден и в сентябре, и в октябре.
export async function getVacationsOverlappingPeriod(periodId, routeIds) {
  if (!routeIds || !routeIds.length) return [];
  const { data: per } = await supabase.from('work_periods').select('date_from, date_to').eq('id', periodId).maybeSingle();
  const chunk = 40; let all = [];
  for (let i = 0; i < routeIds.length; i += chunk) {
    let q = supabase.from('vacation_plans').select('*').in('route_id', routeIds.slice(i, i + chunk));
    if (per) q = q.lte('date_from', per.date_to).gte('date_to', per.date_from); // пересечение диапазонов
    const { data, error } = await q;
    if (error) throw error;
    all = all.concat(data || []);
  }
  return all;
}

export async function getVacationsBySv(periodId, svId) {
  const routes = await getRoutesBySv(svId);
  return getVacationsOverlappingPeriod(periodId, routes.map(r => r.id));
}

// isTmAction: true когда правку вносит сам ТМ (drill-down) — тогда сразу approved
export async function setVacation({ periodId, routeId, dateFrom, dateTo, workingDays, userId, isTmAction = false }) {
  // Достаём текущую подтверждённую версию (если есть) чтобы сохранить как prev_*
  const { data: existing } = await withRetry(() => supabase
    .from('vacation_plans')
    .select('date_from, date_to, working_days, status')
    .eq('period_id', periodId)
    .eq('route_id', routeId)
    .maybeSingle());

  const prevApproved = existing && existing.status === 'approved';

  const payload = {
    period_id:    periodId,
    route_id:     routeId,
    date_from:    dateFrom,
    date_to:      dateTo,
    working_days: workingDays,
    created_by:   userId,
    updated_at:   new Date().toISOString(),
    status:       isTmAction ? 'approved' : 'pending',
  };

  if (isTmAction) {
    payload.approved_by = userId;
    payload.approved_at = new Date().toISOString();
  }

  if (prevApproved) {
    payload.prev_date_from    = existing.date_from;
    payload.prev_date_to      = existing.date_to;
    payload.prev_working_days = existing.working_days;
  }

  // Авто-повтор при кратком сетевом сбое («Failed to fetch»). upsert по
  // onConflict period_id+route_id идемпотентен — повтор не создаёт дублей,
  // а payload (вкл. prev_*) фиксирован до повтора, поэтому данные не искажаются.
  const res = await withRetry(() => supabase
    .from('vacation_plans')
    .upsert(payload, { onConflict: 'period_id,route_id' })
    .select()
    .single());
  return res.data;
}

export async function deleteVacation(periodId, routeId) {
  await withRetry(() => supabase
    .from('vacation_plans')
    .delete()
    .eq('period_id', periodId)
    .eq('route_id', routeId));
}

// ── ПОДТВЕРЖДЕНИЕ ОТПУСКОВ (ТМ) ────────────────────────────────────────────────

export async function approveVacation(vacationId, tmUserId) {
  const { data, error } = await supabase
    .from('vacation_plans')
    .update({
      status: 'approved',
      approved_by: tmUserId,
      approved_at: new Date().toISOString(),
      prev_date_from: null, prev_date_to: null, prev_working_days: null,
    })
    .eq('id', vacationId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function rejectVacation(vacationId, tmUserId, reason = '') {
  const { data, error } = await supabase
    .from('vacation_plans')
    .update({
      status: 'rejected',
      approved_by: tmUserId,
      approved_at: new Date().toISOString(),
      rejected_reason: reason,
    })
    .eq('id', vacationId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Все отпуска ожидающие подтверждения по маршрутам ТМ
export async function getPendingVacationsByRouteIds(periodId, routeIds) {
  if (!routeIds.length) return [];
  const { data, error } = await supabase
    .from('vacation_plans')
    .select('*, routes!route_id(internal_code, merchandisers(full_name))')
    .eq('period_id', periodId)
    .eq('status', 'pending')
    .in('route_id', routeIds);
  if (error) throw error;
  return data || [];
}

// Загрузка отпусков из Excel-файла (массив строк)
// Загрузка отпусков из CSV/Excel файла
// rows: [{ sv_code, mrc_name, date_from, date_to, working_days }]
export async function importVacationsFromFile(periodId, rows, userId) {
  const svCodes = [...new Set(rows.map(r => r.sv_code))];
  const { data: svList } = await supabase
    .from('supervisors')
    .select('id, external_code')
    .in('external_code', svCodes);

  const svCodeToId = Object.fromEntries((svList || []).map(s => [s.external_code, s.id]));
  const svIds = Object.values(svCodeToId);
  const { data: routeList } = await supabase
    .from('routes')
    .select('id, sv_id, merchandisers(full_name)')
    .in('sv_id', svIds);

  const mrcKey = (svId, name) => svId + '||' + name.trim().toLowerCase();
  const mrcToRoute = {};
  for (const route of routeList || []) {
    const m = route.merchandisers;
    const mrcList = m ? (Array.isArray(m) ? m : [m]) : [];
    for (const mrc of mrcList) {
      if (mrc?.full_name) mrcToRoute[mrcKey(route.sv_id, mrc.full_name)] = route.id;
    }
  }

  const toInsert = [];
  for (const row of rows) {
    const svId = svCodeToId[row.sv_code];
    if (!svId) continue;
    const routeId = mrcToRoute[mrcKey(svId, row.mrc_name)];
    if (!routeId) continue;

    // Смотрим текущую подтверждённую версию (для diff)
    const { data: existing } = await supabase
      .from('vacation_plans')
      .select('date_from, date_to, working_days, status')
      .eq('period_id', periodId)
      .eq('route_id', routeId)
      .maybeSingle();

    const entry = {
      period_id:    periodId,
      route_id:     routeId,
      date_from:    row.date_from,
      date_to:      row.date_to,
      working_days: row.working_days || null,
      created_by:   userId,
      updated_at:   new Date().toISOString(),
      status:       'pending',
    };
    if (existing && existing.status === 'approved') {
      entry.prev_date_from    = existing.date_from;
      entry.prev_date_to      = existing.date_to;
      entry.prev_working_days = existing.working_days;
    }
    toInsert.push(entry);
  }

  if (toInsert.length) {
    const { error } = await supabase
      .from('vacation_plans')
      .upsert(toInsert, { onConflict: 'period_id,route_id' });
    if (error) throw new Error(error.message);
  }
  return { imported: toInsert.length, total: rows.length };
}


// ── ЗАКАЗ (order) — импорт из файла и чтение ──────────────────────────────────
// Файл «Заказ» даётся на месяц. Сопоставление маршрута: по internal_code
// (код вида 1054-00001), с запасным вариантом по external_code.
// rows: [{ route_code, work_date, visits }]
export async function importOrders(periodId, rows) {
  const routes = await selectAll('routes', 'id, internal_code, external_code');

  const byInternal = {};
  const byExternal = {};
  (routes || []).forEach(r => {
    if (r.internal_code) byInternal[String(r.internal_code)] = r.id;
    if (r.external_code) byExternal[String(r.external_code)] = r.id;
  });

  const dedup = new Map(); // route_id__date → visits
  let unmatched = 0;
  const unmatchedCodes = new Set();
  for (const row of rows) {
    const routeId = byInternal[row.route_code] || byExternal[row.route_code];
    if (!routeId) { unmatched++; unmatchedCodes.add(row.route_code); continue; }
    dedup.set(`${routeId}__${row.work_date}`, {
      period_id:   periodId,
      route_id:    routeId,
      work_date:   row.work_date,
      file_visits: row.visits,   // эталон из файла
      visits:      row.visits,   // действующий заказ (изначально = файл)
      updated_at:  new Date().toISOString(),
    });
  }

  const toInsert = Array.from(dedup.values());

  // ЧИСТАЯ ЗАМЕНА: сначала удаляем все старые заказы по маршрутам из файла за этот
  // период, потом вставляем новые. Иначе пустая ячейка в новом файле (визита нет)
  // не создаёт строку и НЕ перезаписывает значение, оставшееся от прошлой загрузки,
  // — и в сводной появляются «лишние» визиты на отдельных днях.
  const matchedRouteIds = Array.from(new Set(toInsert.map(o => o.route_id)));
  for (let i = 0; i < matchedRouteIds.length; i += 100) {
    const chunk = matchedRouteIds.slice(i, i + 100);
    const { error } = await supabase.from('orders').delete()
      .eq('period_id', periodId).in('route_id', chunk);
    if (error) throw error;
  }

  // Грузим чанками, чтобы не упереться в лимит payload
  const CHUNK = 500;
  for (let i = 0; i < toInsert.length; i += CHUNK) {
    const slice = toInsert.slice(i, i + CHUNK);
    const { error } = await supabase
      .from('orders')
      .upsert(slice, { onConflict: 'period_id,route_id,work_date' });
    if (error) throw error;
  }

  return {
    imported: toInsert.length,
    routesMatched: new Set(toInsert.map(o => o.route_id)).size,
    skipped: unmatched,
    unmatchedSample: Array.from(unmatchedCodes).slice(0, 8),
  };
}

// { route_id: { 'YYYY-MM-DD': { file_visits, visits } } }
export async function getOrdersByRoutes(periodId, routeIds) {
  if (!routeIds.length) return {};
  // Чанки по маршрутам + пагинация: один запрос без .range() режется PostgREST
  // на 1000 строк, и у СВ с >~32 маршрутами заказ молча обрезался (Заказ пустой).
  const rows = await selectByRouteChunks('orders', 'route_id, work_date, file_visits, visits, prev_visits', periodId, routeIds);
  const map = {};
  rows.forEach(o => {
    if (!map[o.route_id]) map[o.route_id] = {};
    map[o.route_id][o.work_date] = { file_visits: o.file_visits, visits: o.visits, prev_visits: o.prev_visits };
  });
  return map;
}

// СВ переносит заказ: меняем действующий visits на конкретный день (file_visits не трогаем)
async function setOrderVisitsNow({ periodId, routeId, date, visits }) {
  await withRetry(() => supabase
    .from('orders')
    .upsert({
      period_id:  periodId,
      route_id:   routeId,
      work_date:  date,
      visits:     visits,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'period_id,route_id,work_date' }));
}

registerOp('setOrderVisits', (p) => setOrderVisitsNow(p));
export async function setOrderVisits(args) {
  return submit('setOrderVisits', args, `ord|${args.periodId}|${args.routeId}|${args.date}`);
}

export async function importServicePct(periodId, rows) {
  // rows: [{ route_external_code, week_number, service_pct, visits_plan, visits_fact }]
  const routes = await selectAll('routes', 'id, external_code'); // без лимита 1000
  const codeToId = Object.fromEntries(routes.map(r => [r.external_code, r.id]));
  const num = (v) => (v == null || isNaN(v)) ? 0 : Number(v);

  // Если один и тот же маршрут+неделя встречается несколько раз — СУММИРУЕМ визиты
  // (а не затираем), service_pct пересчитываем как факт/план.
  const dedupMap = new Map();
  for (const r of rows) {
    const routeId = codeToId[r.route_external_code];
    if (!routeId) continue;
    const key = `${routeId}__${r.week_number}`;
    const prev = dedupMap.get(key);
    if (prev) {
      prev.visits_plan = (prev.visits_plan || 0) + num(r.visits_plan);
      prev.visits_fact = (prev.visits_fact || 0) + num(r.visits_fact);
      prev.service_pct = prev.visits_plan ? Math.round(prev.visits_fact / prev.visits_plan * 100) : null;
    } else {
      dedupMap.set(key, {
        period_id:   periodId,
        route_id:    routeId,
        week_number: r.week_number,
        service_pct: r.service_pct != null ? r.service_pct : null,
        visits_plan: num(r.visits_plan),
        visits_fact: num(r.visits_fact),
        updated_at:  new Date().toISOString(),
      });
    }
  }

  const toInsert = Array.from(dedupMap.values());

  const { error } = await supabase
    .from('service_pct')
    .upsert(toInsert, { onConflict: 'period_id,route_id,week_number' });
  if (error) throw error;
  return toInsert.length;
}

// ── ИМПОРТ SUPERVISOR ─────────────────────────────────────────────────────────
// Полная логика: обновляет supervisors/routes/merchandisers,
// автоматически создаёт аккаунты СВ и ТМ, отслеживает закрытие вакансий,
// сохраняет историю смены МРЧ на маршруте.

export async function importSupervisor(rows) {
  const { generateSvLogin, generateTmLogin } = await import('./translit');

  // СВ с маршрутами у разных ТМ: основной ТМ (где больше маршрутов) оставляет код СВ,
  // для остальных заводится отдельная запись «код~Фамилия ТМ» на того же пользователя.
  {
    const cnt = {};
    rows.forEach(r => {
      if (!r.sv_internal_code || !r.tm_name || r.is_pm) return;
      (cnt[r.sv_internal_code] = cnt[r.sv_internal_code] || {});
      cnt[r.sv_internal_code][r.tm_name] = (cnt[r.sv_internal_code][r.tm_name] || 0) + 1;
    });
    const primary = {};
    Object.keys(cnt).forEach(c => {
      primary[c] = Object.entries(cnt[c]).sort((a, b) => b[1] - a[1])[0][0];
    });
    rows = rows.map(r => {
      if (!r.sv_internal_code || !r.tm_name || r.is_pm) return r;
      if (!primary[r.sv_internal_code] || primary[r.sv_internal_code] === r.tm_name) return r;
      return { ...r, _base_code: r.sv_internal_code, sv_internal_code: r.sv_internal_code + '~' + r.tm_name.split(' ')[0] };
    });
  }

  const report = {
    routesUpdated: 0,
    svAccountsCreated: [],
    tmAccountsCreated: [],
    vacancyClosed: [],
    mrcChanged: [],
  };

  const nowISO = () => new Date().toISOString();

  async function chunkUpsert(table, arr, conflict) {
    for (let i = 0; i < arr.length; i += 500) {
      const { error } = await supabase.from(table).upsert(arr.slice(i, i + 500), { onConflict: conflict });
      if (error) throw error;
    }
  }
  async function chunkUpsertReturning(table, arr, conflict, keyField) {
    const map = {};
    for (let i = 0; i < arr.length; i += 500) {
      const { data, error } = await supabase.from(table).upsert(arr.slice(i, i + 500), { onConflict: conflict }).select();
      if (error) throw error;
      (data || []).forEach(row => { map[row[keyField]] = row.id; });
    }
    return map;
  }
  async function chunkInsert(table, arr) {
    for (let i = 0; i < arr.length; i += 500) {
      const { error } = await supabase.from(table).insert(arr.slice(i, i + 500));
      if (error) throw error;
    }
  }

  // ── 1) Пользователи (существующие) ──
  const { data: existingUsers } = await supabase.from('users').select('id, login, full_name, role');
  const existingLogins = (existingUsers || []).map(u => u.login);
  const tmNameToUserId = {};
  (existingUsers || []).filter(u => u.role === 'tm').forEach(u => { tmNameToUserId[u.full_name] = u.id; });

  // Создаём недостающих ТМ (батч)
  const uniqueTmNames = [...new Set(rows.map(r => r.tm_name).filter(Boolean))];
  const newTmInserts = [];
  for (const name of uniqueTmNames) {
    if (tmNameToUserId[name]) continue;
    const login = generateTmLogin(name, existingLogins);
    existingLogins.push(login);
    newTmInserts.push({ login, full_name: name, role: 'tm', is_active: true });
    report.tmAccountsCreated.push({ login, full_name: name });
  }
  if (newTmInserts.length) {
    const { data, error } = await supabase.from('users').insert(newTmInserts).select();
    if (error) throw error;
    (data || []).forEach(u => { tmNameToUserId[u.full_name] = u.id; });
  }

  // ── 2) Супервайзеры (дедуп по internal_code) ──
  const { data: existingSvs } = await supabase.from('supervisors').select('id, internal_code, full_name, user_id');
  const svByCode = {};
  (existingSvs || []).forEach(s => { svByCode[s.internal_code] = s; });

  const svMap = new Map();
  for (const row of rows) {
    const code = row.sv_internal_code;
    if (!code) continue;
    const newSvName = row.sv_name || `Вакансия_${code}`;
    svMap.set(code, {
      internal_code: code,
      external_code: row.sv_external_code,
      full_name:     newSvName,
      phone:         row.sv_phone,
      email:         row.sv_email,
      sg:            row.sg,
      bu:            row.bu,
      tm_id:         tmNameToUserId[row.tm_name] || null,
      updated_at:    nowISO(),
    });
  }
  const svIdByCode = await chunkUpsertReturning('supervisors', Array.from(svMap.values()), 'internal_code', 'internal_code');

  // Авто-создание/привязка аккаунтов СВ для реальных СВ без учётки.
  // Логин СВ = sv_<код>. Пользователь с таким логином может УЖЕ существовать
  // (например, прошлая загрузка создала аккаунт, но упала до привязки) — тогда
  // не вставляем заново (иначе дубль логина), а привязываем существующего.
  const loginToUserId = {};
  (existingUsers || []).forEach(u => { if (u.login) loginToUserId[u.login.toLowerCase()] = u.id; });

  const splitBase = {};             // код «разнесённой» записи → код основной записи СВ
  rows.forEach(r => { if (r._base_code) splitBase[r.sv_internal_code] = r._base_code; });
  const svAccountInserts = [];      // реально новые — вставляем
  const svUserUpdates = [];         // привязки supervisors.user_id ← user.id
  for (const [code, svData] of svMap) {
    if (splitBase[code]) continue;  // учётка общая с основной записью — привяжем ниже
    const existing = svByCode[code];
    const isNowReal = !svData.full_name.startsWith('Вакансия_');
    const hasUser = existing && existing.user_id;
    if (!isNowReal || hasUser) continue;
    const svId = svIdByCode[code];
    const login = generateSvLogin(code);
    const existingUserId = loginToUserId[login];
    if (existingUserId) {
      // аккаунт уже есть — просто привязываем
      if (svId) svUserUpdates.push({ id: svId, user_id: existingUserId });
    } else {
      existingLogins.push(login);
      svAccountInserts.push({ login, full_name: svData.full_name, role: 'sv', tm_id: svData.tm_id, is_active: true, _code: code });
      report.svAccountsCreated.push({ login, full_name: svData.full_name });
    }
    if (existing && existing.full_name && existing.full_name.startsWith('Вакансия_')) {
      report.vacancyClosed.push({ internal_code: code, old_name: existing.full_name, new_name: svData.full_name });
    }
  }
  if (svAccountInserts.length) {
    const loginToCode = {};
    svAccountInserts.forEach(u => { loginToCode[u.login] = u._code; });
    const clean = svAccountInserts.map(({ _code, ...u }) => u);
    const { data: newUsers, error } = await supabase.from('users').insert(clean).select();
    if (error) throw error;
    (newUsers || []).forEach(u => {
      const svId = svIdByCode[loginToCode[u.login]];
      if (svId) svUserUpdates.push({ id: svId, user_id: u.id });
    });
  }
  // ВАЖНО: обновление существующих строк по id, не вставка. Нельзя использовать
  // upsert с частичным объектом {id, user_id}: Postgres проверяет вставляемую
  // строку на NOT NULL (internal_code) ДО разрешения конфликта по id и падает,
  // даже если строка уже существует. Поэтому — обычный update.
  for (const u of svUserUpdates) {
    const { error } = await supabase.from('supervisors').update({ user_id: u.user_id }).eq('id', u.id);
    if (error) throw error;
  }

  // Разнесённые записи СВ → тот же пользователь, что у основной записи.
  {
    const bases = [...new Set(Object.values(splitBase))];
    if (bases.length) {
      const { data: bs } = await supabase.from('supervisors').select('internal_code, user_id').in('internal_code', bases);
      const uidByBase = {};
      (bs || []).forEach(b => { uidByBase[b.internal_code] = b.user_id; });
      for (const [code, base] of Object.entries(splitBase)) {
        if (!uidByBase[base] || !svIdByCode[code]) continue;
        const { error } = await supabase.from('supervisors').update({ user_id: uidByBase[base] }).eq('id', svIdByCode[code]);
        if (error) throw error;
      }
    }
  }

  // ── 3) Маршруты (дедуп по internal_code) ──
  const routeMap = new Map();
  for (const row of rows) {
    const code = row.route_internal_code;
    if (!code) continue;
    const svId = svIdByCode[row.sv_internal_code];
    if (!svId) continue;
    routeMap.set(code, {
      external_code:  row.route_external_code,
      short_code:     row.route_short_code,
      internal_code:  code,
      route_type:     row.route_type,
      sv_id:          svId,
      is_substitute:  row.is_substitute || false,
      is_pm:          row.is_pm || false,       // #6 — подменный мерч (лист ПМ): только для отпусков
      is_vacancy:     row.is_vacancy || false,
      is_active:      true,
      updated_at:     nowISO(),
    });
  }
  const routeIdByCode = await chunkUpsertReturning('routes', Array.from(routeMap.values()), 'internal_code', 'internal_code');
  report.routesUpdated = routeMap.size;

  // ── 4) МРЧ + история смены (дедуп по route_id) ──
  const existingMrcs = await selectAll('merchandisers', 'route_id, full_name');
  const mrcByRoute = {};
  (existingMrcs || []).forEach(m => { if (m.route_id) mrcByRoute[m.route_id] = m.full_name; });

  const mrcMap = new Map();
  const historyInserts = [];
  for (const row of rows) {
    if (!row.mrc_name) continue;
    const routeId = routeIdByCode[row.route_internal_code];
    if (!routeId) continue;
    const prevName = mrcByRoute[routeId];
    if (prevName && prevName !== row.mrc_name && !prevName.toLowerCase().includes('вакансия')) {
      historyInserts.push({ route_id: routeId, full_name: prevName, changed_at: nowISO() });
      report.mrcChanged.push({ route_internal_code: row.route_internal_code, old_name: prevName, new_name: row.mrc_name });
    }
    mrcMap.set(routeId, {
      full_name:       row.mrc_name,
      phone:           row.mrc_phone,
      email:           row.mrc_email,
      route_id:        routeId,
      weekends:        row.weekends,
      vacancy_request: row.vacancy_request,
      hire_form:       row.hire_form,
      is_vacancy:      row.is_vacancy || false,
      updated_at:      nowISO(),
    });
  }
  if (historyInserts.length) await chunkInsert('merchandiser_history', historyInserts);
  await chunkUpsert('merchandisers', Array.from(mrcMap.values()), 'route_id');

  return report;
}

// ── ВЫГРУЗКА В EXCEL ─────────────────────────────────────────────────────────

export async function getFullDataForExport(periodId, tmId = null) {
  let query = supabase
    .from('daily_statuses')
    .select(`
      work_date,
      status_code,
      routes!route_id(
        internal_code, external_code, route_type,
        supervisors!sv_id(full_name, internal_code, tm_id),
        merchandisers(full_name, weekends)
      )
    `)
    .eq('period_id', periodId);

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

// ── ПОДСЧЁТ ПЕРЕСЕЧЕНИЙ ОТПУСКОВ ─────────────────────────────────────────────

// Считает уникальные дни, где 2+ маршрута из списка одновременно в отпуске
export function calcVacationOverlapDays(routeIds, vacations) {
  const vacsForRoutes = vacations.filter(v => routeIds.includes(v.route_id));
  if (vacsForRoutes.length < 2) return { days: 0, dates: [] };

  const dayCounts = {}; // date -> count of routes on vacation that day

  for (const v of vacsForRoutes) {
    const from = new Date(v.date_from);
    const to   = new Date(v.date_to);
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().split('T')[0];
      dayCounts[key] = (dayCounts[key] || 0) + 1;
    }
  }

  const overlapDates = Object.entries(dayCounts).filter(([, cnt]) => cnt >= 2).map(([date]) => date);
  return { days: overlapDates.length, dates: overlapDates };
}

// Пересечения по неделям (для динамики)
export function calcVacationOverlapByWeek(routeIds, vacations, weeks) {
  const { dates } = calcVacationOverlapDays(routeIds, vacations);
  const result = {};
  for (const [weekNum, weekData] of Object.entries(weeks)) {
    const weekDates = new Set((weekData.days || []).map(d => d.date));
    result[weekNum] = dates.filter(d => weekDates.has(d)).length;
  }
  return result;
}

// ── РМ: ПОЛНЫЙ ОБЗОР ПРОЕКТА ──────────────────────────────────────────────────

export async function getAllTmWithStats(periodId) {
  const { data: tms } = await supabase.from('users').select('*').eq('role', 'tm').eq('is_active', true);
  return tms || [];
}

// ── ИСТОРИЯ МРЧ ────────────────────────────────────────────────────────────────

export async function getMerchandiserHistory(routeId) {
  const { data, error } = await supabase
    .from('merchandiser_history')
    .select('*')
    .eq('route_id', routeId)
    .order('changed_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// ── НЕАКТИВНЫЕ МАРШРУТЫ ─────────────────────────────────────────────────────────

export async function getInactiveRoutes(svId = null) {
  let query = supabase.from('routes').select('*, merchandisers(*)').eq('is_active', false);
  if (svId) query = query.eq('sv_id', svId);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

// ── СВ ЗАКРЫВАЕТ ВАКАНСИЮ САМОСТОЯТЕЛЬНО ──────────────────────────────────────
// Не дожидаясь официального SUPERVISOR — по стабильному коду маршрута

export async function closeVacancyByRoute(routeId, mrcFullName) {
  // Проверяем текущее состояние
  const { data: existingMrc } = await supabase
    .from('merchandisers')
    .select('id, full_name')
    .eq('route_id', routeId)
    .maybeSingle();

  // Сохраняем историю если было другое имя
  if (existingMrc && existingMrc.full_name && existingMrc.full_name !== mrcFullName) {
    await supabase.from('merchandiser_history').insert({
      route_id: routeId,
      full_name: existingMrc.full_name,
      changed_at: new Date().toISOString(),
    });
  }

  await supabase
    .from('merchandisers')
    .upsert({
      route_id: routeId,
      full_name: mrcFullName,
      is_vacancy: false,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'route_id' });

  await supabase
    .from('routes')
    .update({ is_vacancy: false, updated_at: new Date().toISOString() })
    .eq('id', routeId);

  return { success: true };
}

// ── ПРОГНОЗ SERVICE LEVEL% (СВ вручную) ───────────────────────────────────────

export async function setServiceForecast({ periodId, routeId, weekNumber, forecastPct, userId }) {
  const { data, error } = await supabase
    .from('service_forecasts')
    .upsert({
      period_id:    periodId,
      route_id:     routeId,
      week_number:  weekNumber,
      forecast_pct: forecastPct,
      created_by:   userId,
      updated_at:   new Date().toISOString(),
    }, { onConflict: 'period_id,route_id,week_number' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getServiceForecastsByRoutes(periodId, routeIds) {
  if (!routeIds.length) return [];
  const { data, error } = await supabase
    .from('service_forecasts')
    .select('*')
    .eq('period_id', periodId)
    .in('route_id', routeIds);
  if (error) throw error;
  return data || [];
}

export async function setSvPersonalForecast({ periodId, supervisorId, weekNumber, forecastPct }) {
  const { data, error } = await supabase
    .from('sv_personal_forecasts')
    .upsert({
      period_id:     periodId,
      supervisor_id: supervisorId,
      week_number:   weekNumber,
      forecast_pct:  forecastPct,
      updated_at:    new Date().toISOString(),
    }, { onConflict: 'period_id,supervisor_id,week_number' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getSvPersonalForecast(periodId, supervisorId, weekNumber) {
  const { data, error } = await supabase
    .from('sv_personal_forecasts')
    .select('*')
    .eq('period_id', periodId)
    .eq('supervisor_id', supervisorId)
    .eq('week_number', weekNumber)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function getSvPersonalForecastsBySv(periodId, svIds) {
  if (!svIds.length) return [];
  const { data, error } = await supabase
    .from('sv_personal_forecasts')
    .select('*')
    .eq('period_id', periodId)
    .in('supervisor_id', svIds);
  if (error) throw error;
  return data || [];
}

// ── СВЕРКА: ФАКТ ВИЗИТОВ vs СТАТУСЫ ТАБЕЛЯ ────────────────────────────────────

export async function importDailyVisitFacts(periodId, rows) {
  const routes = await selectAll('routes', 'id, external_code');
  const codeToId = Object.fromEntries(routes.map(r => [r.external_code, r.id]));

  const num = (v) => (v == null || isNaN(v)) ? 0 : Number(v);
  let factTotal = 0, factMatched = 0;           // сумма визитов (Visits Fact) в файле / из них сопоставлено
  let matchedRows = 0;                          // строк с найденным маршрутом
  const unmatchedCodes = new Set();             // уникальные внешние коды маршрутов без совпадения
  let dateFrom = null, dateTo = null;

  const dedupMap = new Map();
  let mergedDuplicates = 0;                       // сколько строк было объединено (несколько на маршрут+дату)
  const stamp = new Date().toISOString();
  for (const r of rows) {
    factTotal += num(r.visits_fact);
    if (r.work_date) {
      if (!dateFrom || r.work_date < dateFrom) dateFrom = r.work_date;
      if (!dateTo   || r.work_date > dateTo)   dateTo   = r.work_date;
    }
    const routeId = codeToId[r.route_external_code];
    if (!routeId) { unmatchedCodes.add(r.route_external_code); continue; }
    matchedRows++;
    factMatched += num(r.visits_fact);
    const key = `${routeId}__${r.work_date}`;
    const prev = dedupMap.get(key);
    if (prev) {
      // Несколько строк на один маршрут за день — СУММИРУЕМ визиты, а не затираем.
      mergedDuplicates++;
      prev.visits_plan = (prev.visits_plan || 0) + num(r.visits_plan);
      prev.visits_fact = (prev.visits_fact || 0) + num(r.visits_fact);
      prev.service_pct = prev.visits_plan ? Math.round(prev.visits_fact / prev.visits_plan * 100) : null;
    } else {
      dedupMap.set(key, {
        period_id:    periodId,
        route_id:     routeId,
        work_date:    r.work_date,
        visits_plan:  num(r.visits_plan),
        visits_fact:  num(r.visits_fact),
        service_pct:  r.service_pct != null ? r.service_pct : null,
        updated_at:   stamp,
      });
    }
  }

  const toInsert = Array.from(dedupMap.values());
  const storedFact = toInsert.reduce((s, x) => s + num(x.visits_fact), 0);
  if (toInsert.length) {
    const { error } = await supabase
      .from('daily_visit_facts')
      .upsert(toInsert, { onConflict: 'period_id,route_id,work_date' });
    if (error) throw error;
  }

  // ЗАКАЗ из этого файла НЕ трогаем. Перезапись заказа вынесена в отдельное
  // действие admin «Перезапись заказа» с явным выбором диапазона дат
  // (overwriteOrdersFromPlan) — иначе отсутствие дня в отфильтрованной выгрузке
  // (только подтверждённые визиты) нельзя отличить от пропуска, и авто-обнуление
  // ломало бы SL. Здесь пишем только факт BI (daily_visit_facts).

  return {
    imported: toInsert.length,               // строк записано (после дедупа маршрут+дата)
    total:    rows.length,                    // строк в файле
    matchedRows,                              // строк с найденным маршрутом (до дедупа)
    skipped:  rows.length - matchedRows,      // строк без совпадения маршрута
    factTotal,                                // сумма Visits Fact в файле
    factMatched,                              // из них сопоставлено с маршрутами
    storedFact,                               // реально записано в базу (после суммирования дублей)
    mergedDuplicates,                         // сколько строк-дублей объединено суммированием
    factLost: factTotal - factMatched,        // потеряно из-за несопоставленных кодов
    unmatchedCount: unmatchedCodes.size,      // сколько уникальных кодов не нашлось
    unmatchedSample: Array.from(unmatchedCodes).slice(0, 15),
    dateFrom, dateTo,
  };
}

// ПЕРЕЗАПИСЬ ЗАКАЗА из план-факт файла по ЯВНО выбранному диапазону дат [fromISO..toISO].
// Admin сам говорит «эти даты перезаписываю целиком», поэтому отсутствие маршрута/дня
// в файле трактуется как 0 (намеренно, безопасно для SL) — ЭТО КАСАЕТСЯ И МАРШРУТОВ,
// которых в файле нет ВООБЩЕ НИ ОДНОЙ СТРОКОЙ (план=0 на всю неделю — например, выгрузка
// отфильтрована по VISIT_IS_PLANED=yes и такие маршруты туда просто не попали). Поэтому
// область обработки — ВСЕ активные маршруты, а не только встреченные в файле (было
// багом до v79: маршрут, отсутствующий в файле целиком, вообще не трогался, и старый
// заказ по нему оставался висеть — искажая недельные итоги на величину такого «хвоста»).
// Прежнее значение заказа сохраняется в prev_visits (в табеле мелко сверху).
// Эталон file_visits не трогаем.
// rows: [{ route_external_code, work_date, visits_plan, ... }] (из parseVisitFactsFile).
export async function overwriteOrdersFromPlan(periodId, rows, fromISO, toISO) {
  if (!fromISO || !toISO || fromISO > toISO) throw new Error('Некорректный диапазон дат');
  const routes = await selectAll('routes', 'id, external_code, is_active');
  const codeToId = Object.fromEntries((routes || []).map(r => [r.external_code, r.id]));
  const num = (v) => (v == null || isNaN(v)) ? 0 : Number(v);

  // План из файла, только в пределах диапазона, свёрнутый по маршрут+дата.
  const planByKey = new Map();      // route_id__date -> visits_plan
  const routeIdsInFile = new Set();
  const unmatched = new Set();
  for (const r of rows) {
    if (!r.work_date || r.work_date < fromISO || r.work_date > toISO) continue;
    const rid = codeToId[r.route_external_code];
    if (!rid) { unmatched.add(r.route_external_code); continue; }
    routeIdsInFile.add(rid);
    const key = `${rid}__${r.work_date}`;
    planByKey.set(key, (planByKey.get(key) || 0) + num(r.visits_plan));
  }

  // Область обработки: ВСЕ активные маршруты (не только встреченные в файле).
  const ids = (routes || []).filter(r => r.is_active).map(r => r.id);
  if (!ids.length) {
    return { overwritten: 0, zeroed: 0, routes: 0, routesInFile: routeIdsInFile.size, from: fromISO, to: toISO, unmatched: Array.from(unmatched).slice(0, 8) };
  }

  // Существующие заказы этих маршрутов в диапазоне — для prev_visits и сохранения file_visits.
  const existing = {};
  for (let i = 0; i < ids.length; i += 60) {
    const chunk = ids.slice(i, i + 60);
    const { data } = await supabase.from('orders')
      .select('route_id, work_date, visits, file_visits, prev_visits')
      .eq('period_id', periodId).in('route_id', chunk)
      .gte('work_date', fromISO).lte('work_date', toISO);
    (data || []).forEach(o => { existing[`${o.route_id}__${o.work_date}`] = o; });
  }

  // Все даты диапазона.
  const dates = [];
  for (let d = new Date(fromISO + 'T00:00:00'); ; d.setDate(d.getDate() + 1)) {
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    dates.push(iso);
    if (iso >= toISO) break;
  }

  const stamp = new Date().toISOString();
  const payload = [];
  let overwritten = 0, zeroed = 0;
  for (const rid of ids) {
    for (const iso of dates) {
      const key = `${rid}__${iso}`;
      const planned = planByKey.has(key) ? planByKey.get(key) : 0;
      const old = existing[key];
      const oldVisits = old ? Number(old.visits) : null;
      if (!old && planned === 0) continue;                 // нечего писать
      if (old && oldVisits === planned) continue;          // без изменений
      const changed = oldVisits !== planned;
      payload.push({
        period_id: periodId, route_id: rid, work_date: iso,
        visits: planned,
        file_visits: old ? old.file_visits : planned,
        prev_visits: changed ? oldVisits : (old ? (old.prev_visits ?? null) : null),
        updated_at: stamp,
      });
      if (planned > 0) overwritten++;
      else if (oldVisits > 0) zeroed++;
    }
  }
  for (let i = 0; i < payload.length; i += 500) {
    const { error } = await supabase.from('orders')
      .upsert(payload.slice(i, i + 500), { onConflict: 'period_id,route_id,work_date' });
    if (error) throw error;
  }
  return { overwritten, zeroed, routes: ids.length, routesInFile: routeIdsInFile.size, from: fromISO, to: toISO, unmatched: Array.from(unmatched).slice(0, 8) };
}

// Прогноз по дням для ОДНОГО маршрута (для раскрытия в сводной прогноза).
// Возвращает { 'YYYY-MM-DD': { order, plan, fact, code } }.
// frozen=true — статусы/заказ из снимка «заморозки», факт всегда живой.
export async function getRouteDayForecast(periodId, routeId, frozen = false) {
  const ordersTbl = frozen ? 'frozen_orders' : 'orders';
  const statusTbl = frozen ? 'frozen_statuses' : 'daily_statuses';
  const [ord, sts, fct] = await Promise.all([
    supabase.from(ordersTbl).select('work_date, visits').eq('period_id', periodId).eq('route_id', routeId),
    supabase.from(statusTbl).select('work_date, status_code, plan_visits').eq('period_id', periodId).eq('route_id', routeId),
    supabase.from('daily_visit_facts').select('work_date, visits_fact').eq('period_id', periodId).eq('route_id', routeId),
  ]);
  const byDate = {};
  (ord.data || []).forEach(o => { (byDate[o.work_date] = byDate[o.work_date] || {}).order = o.visits || 0; });
  (fct.data || []).forEach(f => { (byDate[f.work_date] = byDate[f.work_date] || {}).fact = f.visits_fact; });
  (sts.data || []).forEach(s => {
    const d = (byDate[s.work_date] = byDate[s.work_date] || {});
    const code = s.status_code;
    let plan;
    if (!code || code === 'ПЛ ВЫХ') plan = null;
    else if (/[-−]$/.test(code)) plan = 0;
    else plan = s.plan_visits != null ? s.plan_visits : (d.order != null ? d.order : 0);
    d.plan = plan; d.code = code;
  });
  return byDate;
}

// Заморозка: данные по гипперам из снимка (order/plan из frozen_hyper_daily),
// факт всегда живой (из hyper_daily). Возвращает { point_id: { date: {order,plan,fact} } }.
export async function getFrozenHyperDaily(periodId, pointIds) {
  const daily = {};
  if (!pointIds || !pointIds.length) return daily;
  for (let i = 0; i < pointIds.length; i += 40) {
    const chunk = pointIds.slice(i, i + 40);
    const [fr, live] = await Promise.all([
      selectAll('frozen_hyper_daily', 'hyper_point_id, work_date, order_visits, plan_visits', q => q.eq('period_id', periodId).in('hyper_point_id', chunk)),
      selectAll('hyper_daily', 'hyper_point_id, work_date, fact', q => q.eq('period_id', periodId).in('hyper_point_id', chunk)),
    ]);
    (fr || []).forEach(r => { (daily[r.hyper_point_id] || (daily[r.hyper_point_id] = {}))[r.work_date] = { order: r.order_visits, plan: r.plan_visits, fact: null }; });
    (live || []).forEach(r => { const d = (daily[r.hyper_point_id] || (daily[r.hyper_point_id] = {})); (d[r.work_date] || (d[r.work_date] = { order: 0, plan: null, fact: null })).fact = r.fact; });
  }
  return daily;
}

// Время последней заморозки за период (для подписи «заморожено ВТ 10:00 ДД.ММ»).
export async function getFreezeInfo(periodId) {
  const { data } = await supabase.from('frozen_statuses').select('frozen_at').eq('period_id', periodId).order('frozen_at', { ascending: false }).limit(1).maybeSingle();
  return data?.frozen_at || null;
}

// Ручная заморозка сейчас (кнопка в админке). Возвращает метку времени.
export async function freezeNow(periodId) {
  const { data, error } = await supabase.rpc('freeze_forecast', { p_period_id: periodId });
  if (error) throw error;
  return data;
}

// Удаляет факт визитов за период (для чистой перезаливки после исправления дат).
// Если заданы from/to (YYYY-MM-DD) — только этот диапазон, иначе весь период.
export async function clearVisitFactsForPeriod(periodId, from, to) {
  let q = supabase.from('daily_visit_facts').delete({ count: 'exact' }).eq('period_id', periodId);
  if (from) q = q.gte('work_date', from);
  if (to)   q = q.lte('work_date', to);
  const { error, count } = await q;
  if (error) throw error;
  return count || 0;
}

const EXIT_CODES = new Set(['1', 'СВ']);
const ABSENCE_CODES = new Set(['БЛ-','БЛ+МР','БЛ+ПМ','БЛ+ВЫХ','ОТ-','ОТ+МР','ОТ+ПМ','ОТ+ВЫХ','ДО-','ДО+МР','ДО+ПМ','ДО+ВЫХ','ВК-','ВК+МР','ВК+ПМ','ВК+ВЫХ']);

// Возвращает список расхождений: [{ route_id, date, statusInTabel, visitsFact, type }]
export function computeReconciliation(routeIds, statuses, visitFacts) {
  const discrepancies = [];
  visitFacts.forEach(vf => {
    if (!routeIds.includes(vf.route_id)) return;
    const status = statuses[vf.route_id]?.[vf.work_date];
    const factVisited = (vf.visits_fact || 0) > 0;

    if (EXIT_CODES.has(status) && !factVisited) {
      discrepancies.push({ route_id: vf.route_id, date: vf.work_date, status, visitsFact: vf.visits_fact, type: 'exit_no_fact' });
    } else if (ABSENCE_CODES.has(status) && factVisited) {
      discrepancies.push({ route_id: vf.route_id, date: vf.work_date, status, visitsFact: vf.visits_fact, type: 'absent_but_visited' });
    } else if (!status && factVisited) {
      discrepancies.push({ route_id: vf.route_id, date: vf.work_date, status: null, visitsFact: vf.visits_fact, type: 'unmarked_visit' });
    }
  });
  return discrepancies.sort((a, b) => b.date.localeCompare(a.date));
}

export async function getVisitFactsByRoutes(periodId, routeIds) {
  if (!routeIds.length) return [];
  // мелкими пачками маршрутов, чтобы URL не рубился 400-м
  return selectByRouteChunks('daily_visit_facts', '*', periodId, routeIds);
}

// Универсальный чанк-ридер по маршрутам (для страниц): period_id + in(route_id) пачками.
export async function getRowsByRouteChunks(table, columns, periodId, routeIds) {
  if (!routeIds.length) return [];
  return selectByRouteChunks(table, columns, periodId, routeIds);
}

// ── ТРИГГЕР: ОТ в табеле без подтверждённого отпуска ─────────────────────────
// Возвращает { byRoute: {route_id: кол-во ОТ-дней без плана}, total, routes }.
// «Подтверждённый» = vacation_plans.status='approved' и день попадает в [date_from..date_to].
export async function getUnplannedVacations(periodId, routeIds, vacations) {
  if (!routeIds.length) return { byRoute: {}, total: 0, routes: 0 };
  const appr = {};
  (vacations || []).forEach(v => {
    if (v.status === 'approved') appr[v.route_id] = { from: v.date_from, to: v.date_to };
  });
  let rows = [];
  for (let i = 0; i < routeIds.length; i += 40) {
    const part = routeIds.slice(i, i + 40);
    const { data } = await supabase.from('daily_statuses')
      .select('route_id, work_date, status_code')
      .eq('period_id', periodId).in('route_id', part).like('status_code', 'ОТ%');
    rows = rows.concat(data || []);
  }
  const byRoute = {};
  rows.forEach(r => {
    const p = appr[r.route_id];
    const covered = p && r.work_date >= p.from && r.work_date <= p.to;
    if (!covered) byRoute[r.route_id] = (byRoute[r.route_id] || 0) + 1;
  });
  const total = Object.values(byRoute).reduce((a, b) => a + b, 0);
  return { byRoute, total, routes: Object.keys(byRoute).length };
}

// ── "ГИППЕРА" — ТОЧКИ ВНУТРИ МАРШРУТА ─────────────────────────────────────────

export async function importHyperPoints(rows) {
  const routes = await selectAll('routes', 'id, external_code');
  const codeToId = Object.fromEntries(routes.map(r => [r.external_code, r.id]));

  const toInsert = rows
    .filter(r => codeToId[r.route_external_code])
    .map(r => ({
      route_id:   codeToId[r.route_external_code],
      ship_to:    r.ship_to,
      client:     r.client,
      address:    r.address,
      priority:   r.priority,
      concat_key: r.concat_key,
      is_active:  true,
      updated_at: new Date().toISOString(),
    }));

  if (toInsert.length) {
    const { error } = await supabase.from('hyper_points').upsert(toInsert, { onConflict: 'route_id,ship_to' });
    if (error) throw error;
  }
  return { imported: toInsert.length, total: rows.length, skipped: rows.length - toInsert.length };
}

export async function getHyperPointsByRoutes(routeIds) {
  if (!routeIds.length) return [];
  const { data, error } = await supabase
    .from('hyper_points')
    .select('*')
    .in('route_id', routeIds)
    .eq('is_active', true);
  if (error) throw error;
  return data || [];
}

// ── ГИППЕРЫ ДЛЯ ДАШБОРДА ТМ/РМ ──────────────────────────────────────────────
// Грузит точки-гипперы и их дни (order/plan/fact) по набору маршрутов.
// Возвращает { pointsByRoute: {route_id:[point,...]}, daily: {point_id:{date:{order,plan,fact}}} }
export async function getHyperDataForRoutes(periodId, routeIds) {
  if (!routeIds.length) return { pointsByRoute: {}, daily: {} };
  // Точки грузим мелкими пачками маршрутов (по 40), чтобы URL не рубился 400-м.
  let points = [];
  for (let i = 0; i < routeIds.length; i += 40) {
    const part = routeIds.slice(i, i + 40);
    const pts = await safeHyper('hyper_points', () =>
      selectAll('hyper_points', 'id, route_id, ship_to, client, address', q => q.in('route_id', part).eq('is_active', true)));
    points = points.concat(pts || []);
  }
  const pointsByRoute = {};
  points.forEach(p => { (pointsByRoute[p.route_id] || (pointsByRoute[p.route_id] = [])).push(p); });

  const daily = {};
  const pointIds = points.map(p => p.id);
  for (let i = 0; i < pointIds.length; i += 40) {
    const chunk = pointIds.slice(i, i + 40);
    const rows = await safeHyper('hyper_daily', () =>
      selectAll('hyper_daily', 'hyper_point_id, work_date, order_visits, plan_visits, fact',
        q => q.eq('period_id', periodId).in('hyper_point_id', chunk)));
    (rows || []).forEach(r => {
      (daily[r.hyper_point_id] || (daily[r.hyper_point_id] = {}))[r.work_date] = { order: r.order_visits, plan: r.plan_visits, fact: r.fact };
    });
  }
  return { pointsByRoute, daily };
}

async function safeHyper(label, fn) {
  try { return await fn(); }
  catch (e) { console.error(`[getHyperDataForRoutes] "${label}" не загружен:`, e?.message || e); return null; }
}

// Агрегат гиппера по набору маршрутов за набор дат.
// SL = Факт/Заказ; «ниже нормы» — точки с заказом, где SL < target%.
export function aggregateHyper(routes, dates, hdata, target = 95) {
  const { pointsByRoute, daily } = hdata;
  let order = 0, plan = 0, fact = 0, points = 0, below = 0, withOrder = 0;
  routes.forEach(r => {
    (pointsByRoute[r.id] || []).forEach(p => {
      points++;
      const d = daily[p.id] || {};
      let po = 0, pf = 0;
      dates.forEach(dt => {
        const c = d[dt]; if (!c) return;
        order += c.order || 0; plan += c.plan || 0; fact += c.fact || 0;
        po += c.order || 0; pf += c.fact || 0;
      });
      if (po > 0) { withOrder++; if (pf / po * 100 < target) below++; }
    });
  });
  return {
    points, order, plan, fact, below, withOrder,
    sl: order ? Math.round(fact / order * 100) : null,
    pq: order ? Math.round(plan / order * 100) : null,
    pf: fact ? Math.round(plan / fact * 100) : null,
  };
}

export async function getHyperPointStatuses(periodId, hyperPointIds) {
  if (!hyperPointIds.length) return {};
  const { data, error } = await supabase
    .from('hyper_point_statuses')
    .select('hyper_point_id, work_date, status_code')
    .eq('period_id', periodId)
    .in('hyper_point_id', hyperPointIds);
  if (error) throw error;
  const map = {};
  (data || []).forEach(s => {
    if (!map[s.hyper_point_id]) map[s.hyper_point_id] = {};
    map[s.hyper_point_id][s.work_date] = s.status_code;
  });
  return map;
}

export async function setHyperPointStatus({ periodId, hyperPointId, date, statusCode, userId }) {
  if (statusCode === null) {
    const { error } = await supabase
      .from('hyper_point_statuses')
      .delete()
      .eq('period_id', periodId)
      .eq('hyper_point_id', hyperPointId)
      .eq('work_date', date);
    if (error) throw error;
    return null;
  }
  const { data, error } = await supabase
    .from('hyper_point_statuses')
    .upsert({
      period_id: periodId, hyper_point_id: hyperPointId, work_date: date,
      status_code: statusCode, set_by: userId, updated_at: new Date().toISOString(),
    }, { onConflict: 'period_id,hyper_point_id,work_date' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── ГИППЕРА · ПЛАН(ЗАКАЗ) И ФАКТ ПО ДНЯМ ──────────────────────────────────────
// План(заказ) грузится из файла-календаря, факт — из BI-файла. СВ не редактирует.

async function chunkUpsertHyper(rows) {
  for (let i = 0; i < rows.length; i += 500) {
    const { error } = await supabase.from('hyper_daily')
      .upsert(rows.slice(i, i + 500), { onConflict: 'period_id,hyper_point_id,work_date' });
    if (error) throw error;
  }
}

// parsed = { points:[{route_code, ship_to, ...}], plan:[{route_code, ship_to, work_date}] }
export async function importHyperPlan(periodId, parsed) {
  const { points, plan } = parsed;
  const routes = await selectAll('routes', 'id, internal_code');
  const routeIdByCode = {};
  routes.forEach(r => { if (r.internal_code) routeIdByCode[String(r.internal_code)] = r.id; });

  // upsert точек (дедуп по route_id+ship_to)
  const pointMap = new Map();
  let unmatchedRoutes = 0;
  const unmatchedSample = new Set();
  for (const p of points) {
    const routeId = routeIdByCode[p.route_code];
    if (!routeId) { unmatchedRoutes++; unmatchedSample.add(p.route_code); continue; }
    pointMap.set(routeId + '||' + p.ship_to, {
      route_id: routeId, ship_to: p.ship_to, client: p.client, address: p.address,
      visit_time: p.visit_time, frequency: p.frequency, lat: p.lat, lon: p.lon,
      is_active: true, updated_at: new Date().toISOString(),
    });
  }
  const pointArr = Array.from(pointMap.values());
  const pointIdByKey = {};
  for (let i = 0; i < pointArr.length; i += 500) {
    const { data, error } = await supabase.from('hyper_points')
      .upsert(pointArr.slice(i, i + 500), { onConflict: 'route_id,ship_to' }).select('id, route_id, ship_to');
    if (error) throw error;
    (data || []).forEach(row => { pointIdByKey[row.route_id + '||' + row.ship_to] = row.id; });
  }

  // ЧИСТАЯ ЗАМЕНА точек: по маршрутам из файла гасим точки, которых в новом файле нет
  // (визит/точку убрали) — деактивируем и чистим их дни, чтобы не копились «хвосты».
  const routeIdsInFile = Array.from(new Set(points.map(p => routeIdByCode[p.route_code]).filter(Boolean)));
  const keptIds = new Set(Object.values(pointIdByKey));
  const stale = [];
  for (let i = 0; i < routeIdsInFile.length; i += 100) {
    const chunk = routeIdsInFile.slice(i, i + 100);
    const { data } = await supabase.from('hyper_points')
      .select('id').in('route_id', chunk).eq('is_active', true);
    (data || []).forEach(p => { if (!keptIds.has(p.id)) stale.push(p.id); });
  }
  for (let i = 0; i < stale.length; i += 100) {
    const chunk = stale.slice(i, i + 100);
    await supabase.from('hyper_daily').delete().eq('period_id', periodId).in('hyper_point_id', chunk);
    const { error } = await supabase.from('hyper_points')
      .update({ is_active: false, updated_at: new Date().toISOString() }).in('id', chunk);
    if (error) throw error;
  }

  // строки плана
  const dedup = new Map();
  for (const pl of plan) {
    const routeId = routeIdByCode[pl.route_code];
    if (!routeId) continue;
    const pid = pointIdByKey[routeId + '||' + pl.ship_to];
    if (!pid) continue;
    dedup.set(pid + '__' + pl.work_date, {
      period_id: periodId, hyper_point_id: pid, work_date: pl.work_date, order_visits: 1, updated_at: new Date().toISOString(),
    });
  }
  await chunkUpsertHyper(Array.from(dedup.values()));

  return { points: pointArr.length, planRows: dedup.size, unmatchedRoutes, unmatchedSample: Array.from(unmatchedSample).slice(0, 8) };
}

// rows = [{ sv_code, ship_to, work_date, fact }]
export async function importHyperFact(periodId, rows) {
  const [hpoints, routes, svs] = await Promise.all([
    selectAll('hyper_points', 'id, route_id, ship_to'),
    selectAll('routes', 'id, sv_id'),
    selectAll('supervisors', 'id, external_code'),
  ]);
  const routeSv = {}; routes.forEach(r => { routeSv[r.id] = r.sv_id; });
  const svIdByExt = {}; svs.forEach(s => { if (s.external_code) svIdByExt[String(s.external_code)] = s.id; });
  const byShip = {}; // ship_to -> [{id, sv_id}]
  hpoints.forEach(p => {
    const key = String(p.ship_to);
    (byShip[key] = byShip[key] || []).push({ id: p.id, sv_id: routeSv[p.route_id] });
  });

  const dedup = new Map();
  let skipped = 0;
  for (const row of rows) {
    const cands = byShip[String(row.ship_to)];
    if (!cands || !cands.length) { skipped++; continue; }
    let pid;
    if (cands.length === 1) pid = cands[0].id;
    else {
      const svId = svIdByExt[row.sv_code];
      pid = (cands.find(c => c.sv_id === svId) || cands[0]).id;
    }
    dedup.set(pid + '__' + row.work_date, {
      period_id: periodId, hyper_point_id: pid, work_date: row.work_date, fact: row.fact, updated_at: new Date().toISOString(),
    });
  }
  await chunkUpsertHyper(Array.from(dedup.values()));
  return { imported: dedup.size, skipped, total: rows.length };
}

// { points:[{...route}], daily:{ point_id:{ date:{order,plan,fact} } } }
export async function getHyperTabellBySv(periodId, svId) {
  const routes = await getRoutesBySv(svId);
  const routeIds = routes.map(r => r.id);
  if (!routeIds.length) return { points: [], daily: {}, routesById: {} };
  const { data: pts } = await supabase.from('hyper_points').select('*').in('route_id', routeIds).eq('is_active', true);
  const points = pts || [];
  const pointIds = points.map(p => p.id);
  let daily = {};
  if (pointIds.length) {
    const rows = await selectAll('hyper_daily', 'hyper_point_id, work_date, order_visits, plan_visits, fact, status_code',
      q => q.eq('period_id', periodId).in('hyper_point_id', pointIds));
    rows.forEach(r => {
      if (!daily[r.hyper_point_id]) daily[r.hyper_point_id] = {};
      daily[r.hyper_point_id][r.work_date] = { order: r.order_visits, plan: r.plan_visits, fact: r.fact, code: r.status_code };
    });
  }
  const routesById = {};
  routes.forEach(r => { routesById[r.id] = r; });
  return { points, daily, routesById };
}

// СВ ставит планирование по точке-гипперу (визиты за день)
async function setHyperPlanNow({ periodId, hyperPointId, date, visits, code = undefined }) {
  // #2а — гипперы теперь со статусами выхода (как табель): храним status_code + план.
  const payload = {
    period_id: periodId, hyper_point_id: hyperPointId, work_date: date,
    plan_visits: visits, updated_at: new Date().toISOString(),
  };
  if (code !== undefined) payload.status_code = code;   // null = очистка статуса
  await withRetry(() => supabase
    .from('hyper_daily')
    .upsert(payload, { onConflict: 'period_id,hyper_point_id,work_date' }));
}

registerOp('setHyperPlan', (p) => setHyperPlanNow(p));
export async function setHyperPlan(args) {
  return submit('setHyperPlan', args, `hp|${args.periodId}|${args.hyperPointId}|${args.date}`);
}

// ── ДНЕВНАЯ СВОДКА ДЛЯ "СВЕРКИ" (план vs факт vs SL%) ─────────────────────────

export function computeDaySummary(routeIds, statuses, visitFacts, date) {
  let planned = 0, fact = 0;
  const pcts = [];

  routeIds.forEach(routeId => {
    const status = statuses[routeId]?.[date];
    if (status === '1' || status === 'СВ') planned++;
  });

  visitFacts.forEach(vf => {
    if (!routeIds.includes(vf.route_id) || vf.work_date !== date) return;
    if ((vf.visits_fact || 0) > 0) fact++;
    if (vf.service_pct != null) pcts.push(vf.service_pct);
  });

  const avgService = pcts.length ? pcts.reduce((a, b) => a + b, 0) / pcts.length : null;
  return { planned, fact, avgService };
}

// ── ЭКСПОРТ УЧЁТОК (для Admin) ────────────────────────────────────────────────

export async function getAllActiveUsersForExport() {
  const { data, error } = await supabase
    .from('users')
    .select('login, full_name, role, tm_id')
    .eq('is_active', true)
    .order('role')
    .order('full_name');
  if (error) throw error;

  const tmIds = [...new Set(data.map(u => u.tm_id).filter(Boolean))];
  let tmNames = {};
  if (tmIds.length) {
    const { data: tms } = await supabase.from('users').select('id, full_name').in('id', tmIds);
    tmNames = Object.fromEntries((tms || []).map(t => [t.id, t.full_name]));
  }

  return data.map(u => ({
    login: u.login,
    full_name: u.full_name,
    role: u.role,
    tm_name: u.tm_id ? tmNames[u.tm_id] || '' : '',
  }));
}


// ── СКРЫТИЕ МАРШРУТОВ (тест: только для users.can_hide_routes) ───────────────
export async function canHideRoutes(userId) {
  try {
    const { data } = await supabase.from('users').select('can_hide_routes').eq('id', userId).maybeSingle();
    return !!data?.can_hide_routes;
  } catch { return false; }
}
export async function hideRoute(routeId, hide, actorId) {
  const { error } = await supabase.rpc('hide_route', { p_route_id: routeId, p_hide: hide, p_actor: actorId });
  if (error) throw new Error(error.message);
}
export async function getHiddenRoutes(svId) {
  const { data, error } = await supabase.from('routes').select('*, merchandisers(*)')
    .eq('sv_id', svId).eq('is_hidden', true).order('internal_code');
  if (error) throw new Error(error.message);
  return data || [];
}
